import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import PropTypes from 'prop-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import Routes from 'utils/routes.js.erb';
import { authHeaders } from 'actions';
import useModal from './useModal';
import { errorMessage, customMessage } from 'actions/internal';
import { connect } from 'react-redux';
import PartnerCustomTagField from '../PartnerCustomTagField';
import SupervisorField from '../SupervisorField';

const SubGroupForm = ({group, subgroup, onCustomMessage, onErrorMessage, auth}) => {
  const formMethods = useForm({
    defaultValues: {
      group_id: group.id,
      name: subgroup ? subgroup.name : '',
      asq_responsible_emails: subgroup ? subgroup.asq_responsible_emails : '',
      partner_custom_tag_association: subgroup ? subgroup.partner_custom_tag_association : null,
      supervisor_id: subgroup ? subgroup.supervisor_id : null,
    },
  });

  const { register, formState: { errors }, setError, handleSubmit, clearErrors } = formMethods;
  const subgroup_id = subgroup ? subgroup.id : null;
  const { closeModal } = useModal();
  const queryClient = useQueryClient();

  const save = useMutation({
    mutationFn: (subgroup) => {
      if (subgroup_id) {
        return axios.put(Routes.api_dashboard_administration_organizations_update_subgroup_path({
          subgroup_id: subgroup_id,
        }), { subgroup }, { headers: authHeaders() })
      } else {
        return axios.post(Routes.api_dashboard_administration_organizations_create_subgroup_path(),
          { subgroup }, { headers: authHeaders() })
      }
    },
    onSuccess: () => {
      onCustomMessage('Sub-Group saved successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['administration', 'organization'] }).then(() => {
        closeModal();
      })
    },
    onError: (error) => {
      if (error.response.status === 500) {
        onErrorMessage('Oops! Something went wrong. Contact support for help.');
        return
      }

      if (error.response.status === 422) {
        onErrorMessage('Please check the form for errors and try again.');

        const errors = error.response.data.errors;
        Object.keys(errors).forEach((key) => {
          setError(key, {
            type: 'manual',
            message: errors[key].join(', '),
          });
        });

        return
      }
    },
  })

  // Determines if the partner custom tag field should be visible:
  // - For new subgroups: only visible if the parent group doesn't have a tag
  // - For existing subgroups: visible if the parent group doesn't have a tag AND
  //   none of the subgroup's child cohorts have a tag
  // This prevents tag conflicts in the hierarchy (parent-child relationships)
  const isPartnerCustomTagAssociationVisible = !subgroup ?
    !group.partner_custom_tag_association :
    !group.partner_custom_tag_association &&
    subgroup?.cohorts?.every(cohort => !cohort.partner_custom_tag_association)

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit((data) => {
        clearErrors()

        // rename the key partner_custom_tag_association to partner_custom_tag_association_attributes
        data.partner_custom_tag_association_attributes = data.partner_custom_tag_association
        delete data.partner_custom_tag_association

        // if the partner_custom_tag_association_attributes._destroy is true, then we need to delete the partner_custom_tag_association
        if (data.partner_custom_tag_association_attributes?._destroy) {
          data.partner_custom_tag_association_attributes = {
            ...subgroup.partner_custom_tag_association,
            _destroy: true,
          }
        }

        save.mutate(data)
      })}>
        <div className="row">
          <div className="column">
            <div className="column small-12">
              <div className='o-card__wrap o-admin-card'>
                <div className='u-flex u-flex-big-gap'>
                  <div className='u-flex-1'>
                    {subgroup ? <h3><b>Edit Sub-Group</b></h3> : <h3><b>New Sub-Group</b></h3>}
                  </div>
                </div>
                <div className='u-margin-top--base'>
                  <input type='hidden' {...register('group_id')} />
                  <p>
                    <span className='u-txt--label'>Name</span><br />
                    <input type='text' {...register('name')} aria-invalid={errors.name ? 'true' : 'false'} />
                    {errors.name && <p className='u-txt--error'>{errors.name.message}</p>}
                  </p>
                  <p>
                    <span className='u-txt--label'>Contact Emails (Separate multiple emails with a comma)</span>
                    <br />
                    <span className='u-txt--label'>The addresses contacted around screening and other support families in this entity</span>
                    <br />
                    <input type='text' {...register('asq_responsible_emails')} aria-invalid={errors.asq_responsible_emails ? 'true' : 'false'} />
                    {errors.asq_responsible_emails && <p className='u-txt--error'>{errors.asq_responsible_emails.message}</p>}
                  </p>
                  {auth.isMaxPermissions && (
                    <>
                      {subgroup && (
                        <p>
                          <SupervisorField subgroupId={subgroup.id} />
                        </p>
                      )}
                      {isPartnerCustomTagAssociationVisible && (
                        <p>
                          <PartnerCustomTagField kind="organization_type" label="Organization Type" />
                        </p>
                      )}
                    </>
                  )}
                </div>
                <div className='row'>
                  <div className='column small-12'>
                    <input type='submit' className='o-sp-button o-sp-button--update o-sp-button--primary' value="Save" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

SubGroupForm.propTypes = {
  group: PropTypes.object.isRequired,
  subgroup: PropTypes.object,
  onCustomMessage: PropTypes.func.isRequired,
  onErrorMessage: PropTypes.func.isRequired,
  auth: PropTypes.object.isRequired,
}

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    onErrorMessage: (message) => {
      dispatch(errorMessage(message))
    },
    onCustomMessage: (message, type) => {
      dispatch(customMessage(message, type))
    },
  }
};

export default connect(mapStateToProps, mapDispatchToProps)(SubGroupForm);
